import { baseApi } from 'sama-shared';

/**
 * Comprobante y body del reporte de pago del partner.
 * El reporte en sí va contra la factura (partnerInvoicesApi →
 * POST /partner-invoice/:id/report-payment); `/payout/partner/:id/report-payment`
 * es legado en el backend. Aquí quedan la subida del comprobante y las
 * invalidaciones de tags que usa useFinanceLiveSync.
 */

/** Respuesta de `POST /media/payout-proof/upload` (ImageKit). */
export interface PayoutProofUploadResponse {
  url: string;
  thumbnailUrl?: string;
  fileId?: string;
  filePath?: string;
  name?: string;
}

/**
 * Tres modos excluyentes (PARTNER_PAYOUT_PAYMENT_REPORT.md §Body — tres modos):
 *  1. `payoutAccountId`      → banco, cuenta, cédula y teléfono salen de esa cuenta.
 *  2. `saveAsNewAccount`     → registra los datos como cuenta nueva. Solo TRANSFER.
 *  3. ad-hoc (ninguno)       → los datos valen solo para este reporte.
 */
export interface ReportPartnerPayoutPaymentDto {
  /** 1 = TRANSFER, 2 = PAGO_MOVIL. */
  paymentMethod: number;
  /** Referencia de la transacción. Obligatoria en los tres modos. */
  reference: string;
  /** URL devuelta por la subida del comprobante. Opcional en los tres modos. */
  proofUrl?: string;

  /** Modo 1: cuenta ya registrada en `/payout-account`. */
  payoutAccountId?: string;

  /** Modo 2: guardar estos datos como cuenta nueva. */
  saveAsNewAccount?: boolean;
  /** Modo 2: UUID del banco (no el código). */
  bankId?: string;
  accountOwner?: string;
  accountType?: number;

  /** Modo 3: código del banco emisor, ej. "0102". */
  bankCode?: string;
  /** Modos 2 y 3. En modo 3 solo transferencia — en Pago Móvil da 400. */
  accountNumber?: string;
  /** Modos 2 y 3. */
  nationalId?: string;
  /** Modo 3, solo Pago Móvil — en transferencia da 400. */
  phone?: string;
}

export const partnerPayoutsApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['PartnerPayout', 'PartnerSettlement', 'PartnerDebt'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      /**
       * Paso 1 (opcional): el comprobante viaja aparte para que el reporte
       * pueda mandarse como JSON limpio y para que un fallo de subida no
       * arrastre consigo los datos ya tecleados de la transacción.
       */
      uploadPayoutProof: builder.mutation<PayoutProofUploadResponse, File>({
        query: (file) => {
          const fd = new FormData();
          fd.append('file', file);
          return { url: '/media/payout-proof/upload', method: 'POST', body: fd };
          // Sin Content-Type manual: fetchBaseQuery detecta el FormData.
        },
      }),
    }),
    overrideExisting: false,
  });

export const { useUploadPayoutProofMutation } = partnerPayoutsApi;
